<template>
  <div>
    <div v-if="isOpen" class="search-modal-overlay" @click.self="close" @keydown.esc="close">
      <div class="search-modal-container">
        <div class="search-modal-header">
          <n-auto-complete
            ref="searchInput"
            v-model:value="searchValue"
            :options="formattedOptions"
            placeholder="¿Qué estás buscando? (usa @ para acciones rápidas)"
            @select="handleSelect"
            @update:value="handleInput"
            clearable
            autofocus
            size="large"
          />
          <button class="search-modal-close" @click="close" type="button">Esc</button>
        </div>

        <div v-if="!searchValue && showSuggestions" class="search-modal-suggestions">
          <div class="search-modal-section-title">Sugerencias</div>
          <div
            v-for="(group, groupIndex) in groupedSuggestions"
            :key="groupIndex"
            class="search-modal-group"
          >
            <div class="search-modal-section-title">{{ group.title }}</div>
            <div class="search-modal-list">
              <button
                v-for="(option, index) in group.items"
                :key="index"
                class="search-modal-item"
                :class="{
                  'search-modal-item-active':
                    groupIndex === selectedGroupIndex && index === selectedIndex,
                }"
                @click="selectOption(option)"
                @mouseenter="
                  selectedGroupIndex = groupIndex;
                  selectedIndex = index;
                "
              >
                <Icon
                  v-if="option.icon && typeof option.icon === 'string'"
                  :name="option.icon"
                  class="search-modal-item-icon"
                />
                <span v-else class="search-modal-item-icon">📄</span>
                <span class="search-modal-item-label">{{ option.label }}</span>
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="groupedResults.length > 0" class="search-modal-results">
          <div
            v-for="(group, groupIndex) in groupedResults"
            :key="groupIndex"
            class="search-modal-group"
          >
            <div class="search-modal-section-title">{{ group.title }}</div>
            <div class="search-modal-list">
              <button
                v-for="(option, index) in group.items"
                :key="index"
                class="search-modal-item"
                :class="{
                  'search-modal-item-active':
                    groupIndex === selectedGroupIndex && index === selectedIndex,
                }"
                @click="selectOption(option)"
                @mouseenter="
                  selectedGroupIndex = groupIndex;
                  selectedIndex = index;
                "
              >
                <Icon
                  v-if="option.icon && typeof option.icon === 'string'"
                  :name="option.icon"
                  class="search-modal-item-icon"
                />
                <span v-else class="search-modal-item-icon">📄</span>
                <span class="search-modal-item-label">{{ option.label }}</span>
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="searchValue" class="search-modal-empty">
          <p>No se encontraron resultados para "{{ searchValue }}"</p>
        </div>
      </div>
    </div>
    <OrderSearchModal ref="orderSearchModal" />
    <PedidoActualModal ref="pedidoActualModal" />
  </div>
</template>

<script>
import { NAutoComplete } from "naive-ui";
import Icon from "@/components/theme/ui/Icon.vue";
import { useAuthStore } from "@/stores/auth";
import { useIndoStore } from "@/stores/indo";
import { useDuplicatedOrderStore } from "@/stores/duplicatedOrder";
import { recuperarPedido } from "@/services/requests";
import { mapPedidoToOrderForm } from "@/helpers/orderDuplication";
import { usePdfGafas } from "@/composables/pdfmaker/pdfGafas";
import OrderSearchModal from "./OrderSearchModal.vue";
import PedidoActualModal from "./PedidoActualModal.vue";

export default {
  name: "SearchModal",
  components: {
    NAutoComplete,
    Icon,
    OrderSearchModal,
    PedidoActualModal,
  },
  setup() {
    const authStore = useAuthStore();
    return {
      authStore,
      indoStore: useIndoStore(),
      duplicatedOrderStore: useDuplicatedOrderStore(),
    };
  },
  props: {
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    quickActions: {
      type: Array,
      default: () => [],
    },
    showSuggestions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isOpen: false,
      searchValue: "",
      selectedIndex: 0,
      selectedGroupIndex: 0,
      actionInput: "",
      activeAction: null,
    };
  },
  computed: {
    formattedOptions() {
      return this.filteredOptions.map((opt) => ({
        label: opt.label,
        value: opt.value || opt.label,
        ...opt,
      }));
    },
    filteredOptions() {
      if (!this.searchValue) {
        return [];
      }
      const query = this.searchValue.toLowerCase();
      // Si empieza con "@" es un comando de acción rápida
      if (query.startsWith("@")) {
        const actionQuery = query.substring(1).trim();
        return this.quickActions
          .filter(
            (action) =>
              action.label.toLowerCase().includes(actionQuery) ||
              action.value.toLowerCase().includes(actionQuery),
          )
          .map((action) => ({
            ...action,
            isAction: true,
          }));
      }

      // Buscar tanto en opciones normales como en acciones rápidas
      const normalOptions = this.options
        .filter(
          (option) =>
            option.label.toLowerCase().includes(query) ||
            (option.value && option.value.toLowerCase().includes(query)) ||
            (option.category && option.category.toLowerCase().includes(query)),
        )
        .slice(0, 20);

      // También buscar en acciones rápidas si coinciden
      const matchingActions = this.quickActions
        .filter(
          (action) =>
            action.label.toLowerCase().includes(query) ||
            action.value.toLowerCase().includes(query),
        )
        .map((action) => ({
          ...action,
          isAction: true,
        }));

      // Combinar resultados, priorizando acciones rápidas
      return [...matchingActions, ...normalOptions].slice(0, 20);
    },
    groupedResults() {
      const groups = {};
      this.formattedOptions.forEach((option) => {
        const category = option.category || "other";
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(option);
      });

      return Object.keys(groups).map((category) => ({
        title: this.getCategoryTitle(category),
        items: groups[category],
      }));
    },
    groupedSuggestions() {
      const groups = {
        actions: this.quickActions.map((action) => ({
          ...action,
          isAction: true,
        })),
        pages: this.options.filter((opt) => opt.category === "pages").slice(0, 4),
        settings: this.options.filter((opt) => opt.category === "settings").slice(0, 3),
      };

      return Object.keys(groups)
        .filter((key) => groups[key].length > 0)
        .map((key) => ({
          title: this.getCategoryTitle(key),
          items: groups[key],
        }));
    },
  },
  mounted() {
    this.setupKeyboardListeners();
  },
  beforeUnmount() {
    this.removeKeyboardListeners();
    document.body.style.overflow = "";
  },
  methods: {
    getCategoryTitle(category) {
      const titles = {
        pages: "Páginas",
        settings: "Ajustes",
        actions: "Acciones rápidas",
        other: "Otros",
      };
      return titles[category] || category;
    },
    setupKeyboardListeners() {
      document.addEventListener("keydown", this.handleKeyDown);
    },
    removeKeyboardListeners() {
      document.removeEventListener("keydown", this.handleKeyDown);
    },
    handleKeyDown(event) {
      // No activar en la página de login
      const currentRoute = this.$route?.path || window.location.pathname;
      if (currentRoute === "/login" || currentRoute.startsWith("/login")) {
        return;
      }

      // Cmd+K o Ctrl+K para abrir (solo si no estamos escribiendo en un input)
      const isInputFocused =
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.isContentEditable;

      if ((event.metaKey || event.ctrlKey) && event.key === "k" && !isInputFocused) {
        event.preventDefault();
        this.toggle();
        return;
      }

      // Esc para cerrar
      if (event.key === "Escape" && this.isOpen) {
        event.preventDefault();
        this.close();
        return;
      }

      // Navegación con flechas cuando está abierto
      if (this.isOpen) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          const groups = this.searchValue ? this.groupedResults : this.groupedSuggestions;
          if (groups.length > 0) {
            const currentGroup = groups[this.selectedGroupIndex];
            if (this.selectedIndex < currentGroup.items.length - 1) {
              this.selectedIndex++;
            } else if (this.selectedGroupIndex < groups.length - 1) {
              this.selectedGroupIndex++;
              this.selectedIndex = 0;
            }
          }
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          if (this.selectedIndex > 0) {
            this.selectedIndex--;
          } else if (this.selectedGroupIndex > 0) {
            this.selectedGroupIndex--;
            const groups = this.searchValue ? this.groupedResults : this.groupedSuggestions;
            this.selectedIndex = groups[this.selectedGroupIndex].items.length - 1;
          }
        } else if (event.key === "Enter") {
          event.preventDefault();
          const groups = this.searchValue ? this.groupedResults : this.groupedSuggestions;
          if (groups.length > 0 && groups[this.selectedGroupIndex]) {
            const option = groups[this.selectedGroupIndex].items[this.selectedIndex];
            if (option) {
              this.selectOption(option);
            }
          }
        }
      }
    },
    open() {
      this.isOpen = true;
      this.searchValue = "";
      this.selectedIndex = 0;
      this.selectedGroupIndex = 0;
      this.activeAction = null;
      document.body.style.overflow = "hidden";
      this.$nextTick(() => {
        // Enfocar el input del autocomplete
        const input = this.$el?.querySelector("input");
        if (input) {
          input.focus();
        }
      });
    },
    close() {
      this.isOpen = false;
      this.searchValue = "";
      this.selectedIndex = 0;
      this.selectedGroupIndex = 0;
      this.activeAction = null;
      document.body.style.overflow = "";
    },
    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },
    async selectOption(option) {
      this.$emit("select", option.value, option);

      // Manejar acciones rápidas
      if (option.isAction && option.action) {
        await this.handleQuickAction(option);
        return;
      }

      // Ejecutar acción personalizada si existe
      if (option.action && typeof option.action === "function") {
        option.action();
      }

      // Navegar a ruta si existe
      if (option.route) {
        this.$router.push(option.route);
      }

      this.close();
    },
    async handleQuickAction(action) {
      const actionName = action.action;
      const requiresInput = action.requiresInput !== false;
      let inputValue = null;

      if (requiresInput) {
        inputValue = prompt(`${action.label}\n${action.placeholder || "Ingrese el valor:"}`);
        if (!inputValue) {
          return; // Usuario canceló
        }
      }

      try {
        switch (actionName) {
          case "searchOrder":
            await this.searchOrder(inputValue);
            break;
          case "duplicateOrder":
            await this.duplicateOrder(inputValue);
            break;
          case "duplicateRecipe":
            await this.duplicateRecipe(inputValue);
            break;
          case "printOrder":
            await this.printOrder(inputValue);
            break;
          case "logout":
            await this.logoutUser();
            break;
          case "showCurrentOrder":
            this.$refs.pedidoActualModal?.show();
            break;
          default:
        }
        this.close();
      } catch (error) {
        alert(`Error: ${error.message || "No se pudo completar la acción"}`);
      }
    },
    async searchOrder(query) {
      const profile = this.indoStore.profile;
      if (!profile) {
        throw new Error("No hay perfil de usuario disponible");
      }

      try {
        // Intentar obtener el pedido del año actual primero
        let pedidoCompleto = null;
        const currentYear = new Date().getFullYear();

        // Intentar con el año actual
        try {
          pedidoCompleto = await recuperarPedido(
            {
              CLIENTE: profile.DatosConfig.CUSTOMERNO,
              VKORG: profile.AreaVenta.ORG_VENTA,
              VTWEG: profile.AreaVenta.CANAL_DIST,
              SPART: profile.AreaVenta.SECTOR,
              YEAR: currentYear,
              REF_PEDIDO: query,
              VBELN: query,
            },
            this.authStore.token,
          );
        } catch (error) {
          // Si falla, intentar con el año anterior
          try {
            pedidoCompleto = await recuperarPedido(
              {
                CLIENTE: profile.DatosConfig.CUSTOMERNO,
                VKORG: profile.AreaVenta.ORG_VENTA,
                VTWEG: profile.AreaVenta.CANAL_DIST,
                SPART: profile.AreaVenta.SECTOR,
                YEAR: currentYear - 1,
                REF_PEDIDO: query,
                VBELN: query,
              },
              this.authStore.token,
            );
          } catch (err2) {
            throw new Error("No se pudo encontrar el pedido. Verifique el número o referencia.");
          }
        }

        if (!pedidoCompleto) {
          throw new Error("No se pudo obtener el pedido");
        }

        // La respuesta viene con oClaseBAPI que contiene los datos del pedido
        const orderData = pedidoCompleto.oClaseBAPI || pedidoCompleto;

        if (!orderData) {
          throw new Error("No se encontraron datos del pedido en la respuesta");
        }


        // Abrir modal con los datos del pedido
        this.$nextTick(() => {
          if (this.$refs.orderSearchModal) {
            this.$refs.orderSearchModal.show(orderData);
          } else {
          }
        });
      } catch (error) {
        throw new Error(
          `Error al buscar el pedido: ${error.message || "No se pudo obtener el pedido"}`,
        );
      }
    },
    async duplicateOrder(orderNumber) {
      const profile = this.indoStore.profile;
      if (!profile) {
        throw new Error("No hay perfil de usuario disponible");
      }

      // Obtener datos del pedido
      const pedidoCompleto = await recuperarPedido(
        {
          CLIENTE: profile.DatosConfig.CUSTOMERNO,
          VKORG: profile.AreaVenta.ORG_VENTA,
          VTWEG: profile.AreaVenta.CANAL_DIST,
          SPART: profile.AreaVenta.SECTOR,
          YEAR: new Date().getFullYear(),
          REF_PEDIDO: orderNumber,
          VBELN: orderNumber,
        },
        this.authStore.token,
      );

      if (!pedidoCompleto) {
        throw new Error("No se pudo obtener el pedido");
      }

      // Mapear y guardar datos
      const duplicatedData = mapPedidoToOrderForm(pedidoCompleto);
      localStorage.setItem("duplicatedOrderData", JSON.stringify(duplicatedData));
      this.duplicatedOrderStore.setDuplicatedOrder(duplicatedData);

      // Navegar a pedir lentes
      this.$router.push("/pedir-lentes/datos-basicos");
    },
    async duplicateRecipe(recipeNumber) {
      // Recuperar receta usando el store
      await this.indoStore.RecuperarReceta({
        receta: recipeNumber,
        cliente: this.indoStore.profile?.DatosConfig?.CUSTOMERNO,
      });

      // Navegar a pedir lentes
      this.$router.push("/pedir-lentes/datos-basicos");
    },
    async printOrder(orderNumberOrRef) {
      const profile = this.indoStore.profile;
      if (!profile) {
        throw new Error("No hay perfil de usuario disponible");
      }

      try {
        // Intentar obtener el pedido del año actual primero
        let pedidoCompleto = null;
        const currentYear = new Date().getFullYear();

        // Intentar con el año actual
        try {
          pedidoCompleto = await recuperarPedido(
            {
              CLIENTE: profile.DatosConfig.CUSTOMERNO,
              VKORG: profile.AreaVenta.ORG_VENTA,
              VTWEG: profile.AreaVenta.CANAL_DIST,
              SPART: profile.AreaVenta.SECTOR,
              YEAR: currentYear,
              REF_PEDIDO: orderNumberOrRef,
              VBELN: orderNumberOrRef,
            },
            this.authStore.token,
          );
        } catch (error) {
          // Si falla, intentar con el año anterior
          try {
            pedidoCompleto = await recuperarPedido(
              {
                CLIENTE: profile.DatosConfig.CUSTOMERNO,
                VKORG: profile.AreaVenta.ORG_VENTA,
                VTWEG: profile.AreaVenta.CANAL_DIST,
                SPART: profile.AreaVenta.SECTOR,
                YEAR: currentYear - 1,
                REF_PEDIDO: orderNumberOrRef,
                VBELN: orderNumberOrRef,
              },
              this.authStore.token,
            );
          } catch (err2) {
            throw new Error("No se pudo encontrar el pedido. Verifique el número o referencia.");
          }
        }

        if (!pedidoCompleto) {
          throw new Error("No se pudo obtener el pedido");
        }

        // Generar PDF usando usePdfGafas
        const { generarPDF } = usePdfGafas();
        await generarPDF(false, pedidoCompleto, 1, 1);
      } catch (error) {
        throw new Error(
          `Error al imprimir el pedido: ${error.message || "No se pudo generar el PDF"}`,
        );
      }
    },
    handleSelect(value) {
      const selectedOption = this.formattedOptions.find((opt) => opt.value === value);
      if (selectedOption) {
        this.selectOption(selectedOption);
      }
    },
    handleInput(value) {
      this.searchValue = value;
      this.selectedIndex = 0;
      this.$emit("input", value);
    },
    async logoutUser() {
      try {
        await this.authStore.Logout();
        this.$router.push({ name: "login" });
      } catch (error) {
        alert("No se pudo cerrar la sesión. Inténtalo de nuevo.");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.search-modal-container {
  background: #ffffff;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.15),
    0 10px 10px -5px rgba(0, 0, 0, 0.1);
  animation: slideDown 0.2s ease-out;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

@keyframes slideDown {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.search-modal-header {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;

  :deep(.n-auto-complete) {
    flex: 1;
  }

  :deep(.n-base-selection) {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(0, 0, 0, 0.15);
    color: #000;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      border-color: rgba(0, 0, 0, 0.25);
    }
  }

  :deep(.n-input__input-el) {
    color: #000;
    font-size: 16px;

    &::placeholder {
      color: rgba(0, 0, 0, 0.5);
    }
  }

  :deep(.n-base-selection__border) {
    border-color: rgba(0, 0, 0, 0.15);
  }
}

.search-modal-close {
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  color: rgba(0, 0, 0, 0.6);
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #000;
  }
}

.search-modal-suggestions,
.search-modal-results {
  padding: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.search-modal-group {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.search-modal-section-title {
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.search-modal-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.search-modal-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(0, 0, 0, 0.9);
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background 0.15s;

  &:hover,
  &.search-modal-item-active {
    background: rgba(0, 0, 0, 0.05);
  }
}

.search-modal-item-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.7);

  :deep(svg) {
    width: 100%;
    height: 100%;
  }
}

.search-modal-item-label {
  flex: 1;
  font-size: 14px;
}

.search-modal-empty {
  padding: 32px;
  text-align: center;
  color: rgba(0, 0, 0, 0.5);
  font-size: 14px;
}
</style>
